import { compact } from 'lodash-es';
import { LP_SETTLEMENT_TABS } from './LpSettlement/lpSettlementTabs';
import {
  getOperationsLayoutOptions,
  getPortfolioLayoutOptions,
  getPortfolioLiteLayoutOptions,
} from './PortfolioManagement/layoutConfiguration/getPortfolioLayoutOptions';
import { getReportsBasePathForPathname, parsePortfolioReportRoute } from './components/Reports/reportRoutes';
import { PMS_REPORT_TYPE_TITLES, type PMSReportType } from './components/Reports/reportTypes';

/**
 * Gets the label for an LP Settlement sub-tab route by looking it up in the TABS definition
 */
function getLpSettlementSubTabLabel(route: string): string | undefined {
  const tab = LP_SETTLEMENT_TABS.find(t => t.route === route);
  return tab?.label;
}

export type ResolvePortfolioBreadcrumbOptions = {
  /** Saved reports keyed by type; used to resolve titles for `/saved/:id` routes until hydrated. */
  savedReports?: Partial<Record<PMSReportType, ReadonlyArray<{ id: string; type: PMSReportType }>>>;
};

function resolveOpenReportBreadcrumbTitle(
  pathname: string,
  options?: ResolvePortfolioBreadcrumbOptions
): string | undefined {
  if (getReportsBasePathForPathname(pathname) == null) {
    return undefined;
  }

  const parsed = parsePortfolioReportRoute(pathname);
  if (parsed == null) {
    return undefined;
  }

  switch (parsed.mode) {
    case 'run':
      return PMS_REPORT_TYPE_TITLES[parsed.reportType];
    case 'saved': {
      for (const reports of Object.values(options?.savedReports ?? {})) {
        const found = reports?.find(report => report.id === parsed.reportId);
        if (found) {
          return PMS_REPORT_TYPE_TITLES[found.type];
        }
      }
      return undefined;
    }
    case 'list':
      return undefined;
    default: {
      const _exhaustive: never = parsed;
      return _exhaustive;
    }
  }
}

/**
 * Resolves Portfolio breadcrumb labels using the layout options configuration.
 * Tries both full and lite layout options to find matching routes.
 *
 * @param path - The pathname to resolve (e.g., '/operations/treasury-management/lp-settlement/active-batches')
 * @returns Array of breadcrumb labels, or null if not a Portfolio or Operations dashboard route
 */
export function resolvePortfolioBreadcrumb(
  path: string,
  options?: ResolvePortfolioBreadcrumbOptions
): (string | undefined)[] | null {
  const pathSegments = path.split('/').filter(Boolean);
  const root = pathSegments[0];
  if (pathSegments.length < 2 || (root !== 'portfolio' && root !== 'operations')) {
    return null;
  }

  const viewRoute = pathSegments[1]; // e.g., "treasury-management"
  const tabRoute = pathSegments[2]; // e.g., "lp-settlement"
  const subTabRoute = pathSegments[3]; // e.g., "active-batches" (for LP Settlement)

  const layoutOptionsList =
    root === 'operations'
      ? [getOperationsLayoutOptions()]
      : [getPortfolioLayoutOptions(), getPortfolioLiteLayoutOptions()];

  for (const layoutOptions of layoutOptionsList) {
    const view = layoutOptions.find(option => option.route === `/${viewRoute}`);
    if (!view) {
      continue;
    }

    const parts: (string | undefined)[] = [root, view.label];

    // If there's a tab route, try to find it in the view's tabs
    if (tabRoute) {
      const tab = view.tabs.find(t => t.route === `/${tabRoute}`);
      if (tab) {
        parts.push(tab.label);

        // For LP Settlement, check if there's a sub-tab route
        if (tabRoute === 'lp-settlement' && subTabRoute) {
          const subTabLabel = getLpSettlementSubTabLabel(subTabRoute);
          if (subTabLabel) {
            parts.push(subTabLabel);
          } else {
            parts.push(subTabRoute);
          }
        }

        const openReportTitle = resolveOpenReportBreadcrumbTitle(path, options);
        if (openReportTitle) {
          parts.push(openReportTitle);
        }
      } else {
        // Tab not found in layout options, use route segment as fallback
        parts.push(tabRoute);
      }
    }

    return compact(parts);
  }

  return null;
}
